import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err, "Could not send the reset link. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans flex items-center justify-center px-5 py-16">
      <div className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(circle at 70% 25%, #E2E30A15 0%, transparent 55%)" }} />
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="relative w-full max-w-[420px]"
        data-testid="forgot-password-page"
      >
        <Link to="/login" className="flex items-center gap-3 mb-10 group">
          <Logo className="h-12 w-12" />
          <p className="font-display text-lg uppercase tracking-tight">
            <span className="text-[#E2E30A]">Performance</span> Nutrition
          </p>
        </Link>

        {sent ? (
          <div data-testid="forgot-password-sent">
            <MailCheck className="h-10 w-10 text-[#E2E30A] mb-5" />
            <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter font-black leading-[0.95]">
              Check your <span className="text-[#E2E30A]">inbox.</span>
            </h1>
            <p className="text-sm text-zinc-400 mt-3">
              If that email is registered, a reset link is on its way. It expires in 1 hour and can be used once.
              Don't forget to check your spam folder.
            </p>
            <Link to="/login" data-testid="forgot-back-to-signin"
              className="inline-block mt-8 text-[11px] uppercase tracking-[0.22em] text-[#E2E30A] font-bold hover:text-white transition-colors">
              ← Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl sm:text-5xl uppercase tracking-tighter font-black leading-[0.95]">
              Forgot your <span className="text-[#E2E30A]">password?</span>
            </h1>
            <p className="text-sm text-zinc-400 mt-2">
              Enter the email you signed up with and we'll send you a secure link to set a new one.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">Email</label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@endurance.com"
                  className="mt-1.5 h-12 rounded-none bg-white/5 border-white/12 focus-visible:ring-[#E2E30A] focus-visible:border-[#E2E30A] text-white placeholder:text-zinc-500"
                />
              </div>
              <Button
                type="submit"
                data-testid="forgot-submit"
                disabled={loading}
                className="w-full h-13 sm:h-14 rounded-none bg-[#E2E30A] hover:bg-white disabled:bg-white/10 disabled:text-white/40 text-black font-display font-black uppercase tracking-widest text-sm sm:text-base transition-colors group"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <span className="inline-flex items-center gap-2">
                    Send reset link
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                )}
              </Button>
            </form>
            <Link to="/login" data-testid="forgot-back-to-signin"
              className="inline-block mt-8 text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-bold hover:text-white transition-colors">
              ← Back to sign in
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
